import React, { useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ShieldCheck, Loader2, IdCard, KeyRound } from "lucide-react";
import { toast } from "sonner";

// First-login prompt: issues the Member ID number and sets the security phrase.
export default function OnboardingPrompt() {
  const { user, setUser } = useAuth();
  const [phrase, setPhrase] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const open = !!user && user.role !== "guest" && (!user.member_id || !user.has_security_phrase);
  if (!open && !done) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (phrase.trim().length < 4) { toast.error("Security phrase must be at least 4 characters"); return; }
    if (phrase.trim().toLowerCase() !== confirm.trim().toLowerCase()) { toast.error("Phrases do not match"); return; }
    setBusy(true);
    try { const { data } = await api.post("/auth/onboard", { phrase }); setDone(data.user); setUser(data.user); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={() => {}}>
      <DialogContent className="glass max-w-lg [&>button]:hidden" data-testid="onboarding-prompt">
        {done ? (
          <>
            <DialogHeader><DialogTitle className="font-display text-2xl">Welcome aboard, {done.name?.split(" ")[0]} ⚓</DialogTitle></DialogHeader>
            <div className="rounded-2xl border border-[hsl(var(--gold))]/40 bg-[hsl(var(--gold))]/10 p-5 text-center">
              <p className="label-eyebrow">Your Member ID</p>
              <p className="mt-1 font-mono-data text-3xl font-700 tracking-wider" data-testid="member-id-value">{done.member_id}</p>
              <p className="mt-2 text-xs text-muted-foreground">This device is now trusted. On any new device you'll be asked for your security phrase after your password — impersonators without it are locked out.</p>
            </div>
            <div className="flex gap-2">
              <Button className="flex-1 bg-[hsl(var(--gold))] text-[hsl(var(--primary-foreground))]" onClick={() => { window.location.href = "/profile#id-card"; }} data-testid="onboarding-view-card"><IdCard className="mr-2 h-4 w-4" />View my ID card</Button>
              <Button variant="outline" className="gold-ring" onClick={() => setDone(null)} data-testid="onboarding-close">Continue</Button>
            </div>
          </>
        ) : (
          <form onSubmit={submit}>
            <DialogHeader><DialogTitle className="flex items-center gap-2 font-display text-2xl"><ShieldCheck className="h-6 w-6 text-[hsl(var(--gold))]" />Secure your membership</DialogTitle></DialogHeader>
            <p className="mt-2 text-sm text-muted-foreground">You're about to receive your official <b>Member ID number</b>. Choose a private <b>security word or phrase</b> — it will be required (after your password) whenever you sign in from a new device. Never share it.</p>
            <div className="mt-5 space-y-3">
              <div><Label htmlFor="phrase">Security word or phrase</Label><Input id="phrase" type="password" value={phrase} onChange={(e) => setPhrase(e.target.value)} placeholder="e.g. blue anchor 1972" autoComplete="off" data-testid="phrase-input" /></div>
              <div><Label htmlFor="phrase2">Repeat it</Label><Input id="phrase2" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="off" data-testid="phrase-confirm-input" /></div>
              <p className="text-xs text-muted-foreground">Not case-sensitive. Minimum 4 characters. Remember it — officers cannot see it.</p>
            </div>
            <Button type="submit" disabled={busy} className="mt-5 w-full bg-[hsl(var(--gold))] font-700 text-[hsl(var(--primary-foreground))]" data-testid="onboarding-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><KeyRound className="mr-2 h-4 w-4" />Issue my Member ID</>}</Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
